import { useState, useCallback } from "react";
import { ShieldCheck, ShieldAlert, Loader2, Copy, Check, KeyRound, Smartphone, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoenixWordmark } from "@/components/PhoenixLogo";

const inputCls = "w-full px-3 py-2 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30";

function BackupCodes({ codes }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard?.writeText(codes.join("\n"));
        setCopied(true); setTimeout(() => setCopied(false), 1500);
    };
    return (
        <div data-testid="mfa-gate-backup-codes" className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-amber-900 flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Save your backup codes</p>
                <button onClick={copy} className="text-xs font-semibold text-amber-900 border border-amber-300 rounded-lg px-2.5 py-1 hover:bg-amber-100 inline-flex items-center gap-1">
                    {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy all</>}
                </button>
            </div>
            <p className="text-xs text-amber-800 mb-3">Each code works once if you lose your authenticator. They won't be shown again.</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm text-phoenix-navy">
                {codes.map((c) => <span key={c} className="bg-white border border-amber-200 rounded px-2 py-1 text-center">{c}</span>)}
            </div>
        </div>
    );
}

/** The enrollment flow (QR → backup codes → verify). On success refreshes the
 *  session so mfa_state clears and the gate releases. */
function Enrollment({ onClose }) {
    const { fetchMe } = useAuth();
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const start = async () => {
        setBusy(true); setError("");
        try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
        catch (e) { setError(e?.response?.data?.detail || "Could not start setup."); }
        finally { setBusy(false); }
    };
    const verify = async () => {
        if (code.trim().length < 6) { setError("Enter the 6-digit code from your app."); return; }
        setBusy(true); setError("");
        try {
            await api.post("/auth/mfa/verify-setup", { code: code.trim() });
            toast.success("Two-factor authentication is now ON.");
            await fetchMe();
        } catch (e) { setError(e?.response?.data?.detail || "That code didn't match."); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="mfa-gate-enrollment" className="bg-white border border-border rounded-2xl p-6 space-y-4 shadow-xl">
            {error && <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2"><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}</div>}
            {!setup ? (
                <>
                    <p className="text-sm text-muted-foreground">You'll need an authenticator app (Google Authenticator, Authy, Microsoft Authenticator, or 1Password). After setup you'll enter a 6-digit code at each sign-in.</p>
                    <div className="flex gap-2">
                        <button onClick={start} disabled={busy} data-testid="mfa-gate-start" className="text-sm font-semibold text-white bg-phoenix-navy rounded-lg px-5 py-2.5 hover:bg-phoenix-navy-2 disabled:opacity-50 inline-flex items-center gap-1.5">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />}<ShieldCheck className="w-4 h-4" /> Set up two-factor authentication
                        </button>
                        {onClose && <button onClick={onClose} className="text-sm font-semibold text-muted-foreground px-4 py-2.5">Later</button>}
                    </div>
                </>
            ) : (
                <>
                    <h3 className="font-display font-semibold text-phoenix-navy flex items-center gap-1.5"><Smartphone className="w-4 h-4" /> Scan with your authenticator app</h3>
                    <div className="flex flex-col items-center gap-3">
                        <img src={setup.qr_code_base64} alt="2FA QR code" data-testid="mfa-gate-qr" className="border border-border rounded-lg w-44 h-44" />
                        <p className="text-xs text-muted-foreground">Can't scan? Key: <span className="font-mono text-phoenix-navy break-all">{setup.manual_key}</span></p>
                    </div>
                    <BackupCodes codes={setup.backup_codes} />
                    <div>
                        <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5">Enter the 6-digit code</label>
                        <input data-testid="mfa-gate-code" className={inputCls} placeholder="000000" maxLength={8} value={code} onChange={(e) => setCode(e.target.value)} />
                    </div>
                    <button onClick={verify} disabled={busy} data-testid="mfa-gate-verify" className="w-full text-sm font-semibold text-white bg-phoenix-navy rounded-lg px-5 py-2.5 hover:bg-phoenix-navy-2 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify & turn on 2FA
                    </button>
                </>
            )}
        </div>
    );
}

/** Enforces the organization MFA policy. Hard-gates (blocks the whole portal)
 *  once a staff member's grace window expires; shows a soft reminder while
 *  there's still time. No effect for exempt users (inmates, already-enrolled). */
export function MfaPolicyGate({ children }) {
    const { user } = useAuth();
    const [reminderOpen, setReminderOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const state = user?.mfa_state;

    if (!state || (!state.gated && !state.must_enroll)) return children;

    if (state.gated) {
        return (
            <div data-testid="mfa-gate-blocked" className="min-h-screen bg-phoenix-bg flex items-center justify-center p-4">
                <div className="w-full max-w-md space-y-5">
                    <div className="flex justify-center"><PhoenixWordmark /></div>
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2 text-sm text-red-800">
                        <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
                        <span>Your organization now requires <strong>two-factor authentication</strong> for all staff. Set up 2FA below to continue — it protects patient data (PHI) if your password is ever compromised.</span>
                    </div>
                    <Enrollment />
                </div>
            </div>
        );
    }

    // Soft reminder (still within grace window)
    return (
        <>
            {children}
            {!dismissed && (
                <div data-testid="mfa-gate-reminder" className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white border border-amber-300 rounded-xl shadow-xl p-4">
                    {reminderOpen ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-bold text-phoenix-navy flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Set up 2FA</p>
                                <button onClick={() => setReminderOpen(false)} className="text-muted-foreground hover:text-phoenix-navy"><X className="w-4 h-4" /></button>
                            </div>
                            <Enrollment onClose={() => setReminderOpen(false)} />
                        </div>
                    ) : (
                        <div className="flex items-start gap-2.5">
                            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-phoenix-navy">Two-factor required soon</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Your organization requires 2FA. {state.grace_days_remaining > 0
                                        ? `${state.grace_days_remaining} day${state.grace_days_remaining === 1 ? "" : "s"} left to enroll.`
                                        : "Enroll today to avoid losing access."}
                                </p>
                                <div className="flex gap-2 mt-2">
                                    <button onClick={() => setReminderOpen(true)} data-testid="mfa-reminder-setup" className="text-xs font-semibold text-white bg-phoenix-navy rounded-lg px-3 py-1.5 hover:bg-phoenix-navy-2">Set up now</button>
                                    <button onClick={() => setDismissed(true)} className="text-xs font-semibold text-muted-foreground px-2 py-1.5">Not now</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
